/**
 * @architecture-header 1
 * @layer routes
 * @domain onboarding
 * @responsibility Gate the product shell on an explicit workspace choice, on first login and after leaving.
 * @state-owner parent-provided onboarding callback
 * @allowed-imports shared visual language; React props only
 * @forbidden API client; authorization decisions; changing role without leaving the shell; role persistence
 * @decision This screen is a gate, not a one-time onboarding step. Role is chosen here and cannot
 *   change while the shell is mounted — that is what keeps permissions unambiguous mid-task. It is
 *   reached again by 「切換身分」, which deletes the active context and leaves the session intact, so
 *   coming back costs one click rather than a fresh sign-in.
 * @public-api WorkspaceShell onboarding gate
 * @side-effects role-selection callback only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { WorkspaceRole } from '../shared/lib/model';

/**
 * The role is chosen here, and nowhere else. The shell has no in-product role switch — but that
 * rule is about *when* the role may change, not about how expensive changing it should be: you
 * leave the workspace and come back through this gate, with your sign-in still alive.
 *
 * Rendered whenever there is no active context, so 「切換身分」 needs no navigation of its own; it
 * deletes the context and this screen reappears.
 */
export function RoleSelection({
  busy = false,
  error = null,
  onSelect,
}: {
  busy?: boolean;
  error?: string | null;
  onSelect(role: WorkspaceRole): void;
}) {
  return (
    <main className="entry-screen">
      <div className="joinbox" style={{ width: 'min(460px, 92vw)' }}>
        <div className="jlogo">V</div>
        <h3>你今天要以哪個身分進入？</h3>
        <span>選好之後就會直接進入對應的工作區，產品裡不會再切換身分。</span>
        <div style={{ marginTop: 18, textAlign: 'left' }}>
          <button
            className="opt3"
            disabled={busy}
            style={{ width: '100%' }}
            type="button"
            onClick={() => onSelect('teacher')}
          >
            <b>我是老師</b>
            <span>整理活動、複核題目、帶課，並看每位學生的學習證據。</span>
          </button>
          <button
            className="opt3"
            disabled={busy}
            style={{ width: '100%' }}
            type="button"
            onClick={() => onSelect('student')}
          >
            <b>我是學生</b>
            <span>進入班級、完成老師指派的活動，卡住時先問 AI 助教。</span>
          </button>
        </div>
        {error ? <p className="jmsg">{error}</p> : null}
      </div>
    </main>
  );
}
